import { Badge, Box, Center, Group, Image, Modal, Stack, Text } from '@mantine/core'
import { MRT_ColumnDef } from 'mantine-react-table'
import { memo, useState } from 'react'
import EventPageList from 'src/containers/Event'
import { size } from 'src/variable/constant'
import CustomTooltip from 'src/components/CustomComponents/CustomTooltip'
import UserIdentityChip from 'src/components/UserIdentityChip'
import { formatMoneyVND } from 'src/utils/utils'

interface RepeatType {
	days: string
	type: string
	current_repeat: number
	amount_of_repeat: number
}

const formatTicket = (ticketType?: string, ticketValue?: string) => {
	if (!ticketType || ticketType === 'FREE') return 'Free'
	if (!ticketValue || ticketValue === '0') return '-'

	const [min, max] = String(ticketValue).split(':')
	

	if (min && max) return `${formatMoneyVND(min)} - ${formatMoneyVND(max)}`

	return formatMoneyVND(ticketValue)
}

const formatParticipantType = (type?: string) => {
	if (type === 'OWNER') return 'HOST'
	if (type === 'ADMIN') return 'CO-HOST'
	return type || '-'
}

const formatRepeatType = (type?: string) => {
	if (type === 'BI_WEEK') return 'TWO WEEKS'
	if (type === 'THREE_WEEK') return 'THREE WEEKs'
	return type || '-'
}

function EventsPage() {
	const [previewImage, setPreviewImage] = useState<string | null>(null)

	const columns: MRT_ColumnDef[] = [
		{
			accessorKey: 'no',
			header: 'No',
			size: size.zero,
		},
		{
			accessorKey: 'image',
			header: 'Image',
			size: 116,
			maxSize: 116,
			Cell: ({ cell }) => {
				const image = cell.getValue<string>()
				const imageContent = image ? (
					<Image
						src={image}
						height={64}
						width={96}
						fit="cover"
						radius={8}
						sx={{
							border: '1px solid #e5e7eb',
							backgroundColor: '#f8fafc',
							boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
							transition: 'transform 120ms ease, box-shadow 120ms ease',
							cursor: 'zoom-in',
							'&:hover': {
								transform: 'translateY(-1px)',
								boxShadow: '0 6px 16px rgba(15, 23, 42, 0.14)',
							},
						}}
					/>
				) : (
					<Center
						sx={{
							width: 96,
							height: 64,
							borderRadius: 8,
							border: '1px dashed #cbd5e1',
							backgroundColor: '#f8fafc',
						}}
					>
						<Text size="xs" color="dimmed" weight={600}>
							No image
						</Text>
					</Center>
				)

				return (
					<Box sx={{ width: 104 }}>
						{image ? (
							<Box onClick={() => setPreviewImage(image)}>
								{imageContent}
							</Box>
						) : (
							imageContent
						)}
					</Box>
				)
			},
		},
		{
			accessorKey: 'title',
			header: 'Activity',
			size: 260,
			maxSize: 280,
			Cell: ({ row }) => {
				const title = row.original['title'] || '-'
				const description = row.original['description'] || '-'

				return (
					<CustomTooltip
						label={
							<Stack
								spacing={6}
								sx={{
									whiteSpace: 'normal',
									wordBreak: 'break-word',
								}}
							>
								<Text size="sm" weight={700}>
									{title}
								</Text>
								<Text size="xs">
									{description}
								</Text>
							</Stack>
						}
						multiline
						width={520}
						sx={{
							maxWidth: 520,
							whiteSpace: 'normal',
							wordBreak: 'break-word',
						}}
					>
						<Stack spacing={2} sx={{ maxWidth: 260 }}>
							<Text weight={700} size="sm" lineClamp={1}>
								{title}
							</Text>
							<Text size="xs" color="dimmed" lineClamp={2}>
								{description}
							</Text>
						</Stack>
					</CustomTooltip>
				)
			},
		},
		{
			// The cell is drawn by the list, which owns Approve and Delete.
			accessorKey: 'pending_review',
			header: 'Review',
			size: 110,
			maxSize: 120,
		},
		{
			accessorKey: 'start_time_display',
			header: 'Schedule',
			size: 150,
			maxSize: 170,
			Cell: ({ row }) => {
				const start = row.original['start_time_display'] || 'N/A'
				const end = row.original['end_time_display'] || 'N/A'

				return (
					<Stack spacing={2}>
						<Text size="xs" weight={700}>
							{start}
						</Text>
						<Text size="xs" color="dimmed">
							{end}
						</Text>
					</Stack>
				)
			},
		},
		{
			accessorKey: 'repeat_type',
			header: 'Repeated',
			size: 120,
			maxSize: 140,
			Cell: ({ cell }) => {
				const value = cell.getValue<RepeatType>()
				const repeatType = value?.type
				const amountOfRepeat = value?.amount_of_repeat
				const isRepeat = repeatType && repeatType !== 'NONE'

				if (!isRepeat) {
					return (
						<Badge radius={6} color="gray" variant="light">
							No repeat
						</Badge>
					)
				}

				return (
					<Stack spacing={4}>
						<Badge radius={6} color="blue" variant="light">
							{formatRepeatType(repeatType)}
						</Badge>
						<Text size="xs" weight={600} color="dimmed">
							{amountOfRepeat || '-'} repeats
						</Text>
					</Stack>
				)
			},
		},
		{
			accessorKey: 'remaining_repeat_count',
			header: 'Remaining Repeat',
			size: 130,
			maxSize: 150,
			Cell: ({ cell }) => {
				const remainingRepeat = cell.getValue<number | null | undefined>()
				return (
					<Text size="sm" weight={600}>
						{remainingRepeat ?? '-'}
					</Text>
				)
			},
		},
		{
			accessorKey: 'categories',
			header: 'Categories',
			size: 150,
			maxSize: 180,
			Cell: ({ cell }) => {
				const categories = cell.getValue<string[]>() || []
				if (!categories.length) return <Text size="sm">-</Text>
				return (
					<Group spacing={4} sx={{ maxWidth: 170 }}>
						{categories.slice(0, 2).map((category) => (
							<Badge key={category} radius={6} color="green" variant="light">
								{category}
							</Badge>
						))}
						{categories.length > 2 && (
							<Badge radius={6} color="gray" variant="light">
								+{categories.length - 2}
							</Badge>
						)}
					</Group>
				)
			},
		},
		{
			accessorKey: 'ticket_entrance_type',
			header: 'Ticket',
			size: 140,
			maxSize: 160,
			Cell: ({ row }) => {
				const ticketType = row.original['ticket_entrance_type']
				const ticketValue = row.original['ticket_entrance']
				return (
					<Stack spacing={4}>
						<Badge radius={6} color="orange" variant="light">
							{ticketType || '-'}
						</Badge>
						<Text size="xs" color="dimmed">
							{formatTicket(ticketType, ticketValue)}
						</Text>
					</Stack>
				)
			},
		},
		{
			accessorKey: 'participants',
			header: 'Host/Co-host',
			size: size.zero,
			maxSize: size.no,
			Cell: ({ cell }) => {
				const participants = cell.getValue<{ type: string; user: { name: string, avatar: string, id: string } }[]>()
				return (
					<Stack spacing={4} sx={{ minWidth: 180 }}>
						{participants.map((participant, index) => {
							return (
								<UserIdentityChip
									key={index}
									id={participant?.user?.id}
									name={participant?.user?.name}
									avatar={participant?.user?.avatar}
									badge={formatParticipantType(participant.type)}
									badgeColor={participant.type === 'OWNER' ? 'green' : 'blue'}
								/>
							)
						})}
					</Stack>
				)
			},
		},
		{
			accessorKey: 'amount_of_participant',
			header: 'Interested',
			size: size.zero,
			maxSize: size.no,
			Cell: ({ cell }) => {
				return (
					<CustomTooltip
						label={cell.getValue<string>()}
						maxWidth={size.mediumS}
					/>
				)
			},
		},
		{
			accessorKey: 'address',
			header: 'Location',
			size: size.zero,
			maxSize: size.no,
			Cell: ({ cell }) => {
				return (
					<CustomTooltip
						label={cell.getValue<string>()}
						maxWidth={size.mediumS}
					/>
				)
			},
		},
		{
			accessorKey: 'created_at',
			header: 'Created at',
			size: size.zero,
			maxSize: size.text,
		},
		// {
		// 	accessorKey: 'share_link',
		// 	header: 'Share Link',
		// 	size: size.zero,
		// 	maxSize: size.text,
		// 	Cell: ({ cell }) => {
		// 		const link = cell.getValue<string>()
		// 		if (!link) return null
		// 		return (
		// 			<a href={link} target="_blank" rel="noreferrer noopener">
		// 				{link}
		// 			</a>
		// 		)
		// 	},
		// },
	]
	const fields = [
		{
			value: 'title',
			label: 'Title',
		},
	]
	return (
		<>
			<EventPageList columns={columns} fields={fields} />
			<Modal
				opened={!!previewImage}
				onClose={() => setPreviewImage(null)}
				title="Event image"
				size="xl"
				centered
			>
				{previewImage && (
					<Image
						src={previewImage}
						fit="contain"
						sx={{
							width: '100%',
							maxHeight: '80vh',
							img: {
								maxHeight: '80vh',
								objectFit: 'contain',
							},
						}}
					/>
				)}
			</Modal>
		</>
	)
}

export default memo(EventsPage)
